<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import TheFilter from '@/components/category/organisms/TheFilter.vue'
import { translate } from '@/plugins/i18n.js'
import {
  pushWalkerEvent,
  sanitizeSearchTermForTracking
} from '@/services/helpers/ElbwalkerHelper'
import { NO_INDEX_FOLLOW } from '@/services/helpers/MetaHelper.js'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import { computed, defineAsyncComponent, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const LoadingOverlay = defineAsyncComponent(
  () => import('@/components/basic/atoms/LoadingOverlay.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)
const ManufacturerChip = defineAsyncComponent(
  () => import('@/components/basic/atoms/ManufacturerChip.vue')
)

const searchConfig = useSearchConfig()
const route = useRoute()

const category = computed(() => searchConfig.category)
const categoryError = computed(() => searchConfig.config.error)
const manufacturerHits = computed(() => searchConfig.manufacturerHits)
const searchTerm = computed(() => route.query.q ?? '')
const isLoading = computed(() => searchConfig.categoryIsLoading)

const elbGlobals = computed(() => {
  const base = 'page_type:search;search_version:opensearch'
  const term = sanitizeSearchTermForTracking(searchTerm.value)
  return term ? `${base};search_term:${term}` : base
})

const headline = computed(() => {
  if (!searchTerm.value) return translate('fe_app_search_results')
  // Count only once results are in; while loading show the plain headline.
  if (searchConfig.categoryIsLoading || !category.value.code) {
    return translate('fe_app_search_results_for', searchTerm.value)
  }
  const total = searchConfig.getConfig.totalCount
  if (total === 0) {
    return translate('fe_app_search_results_for_none', searchTerm.value)
  }
  if (total === 1) {
    return translate('fe_app_search_results_for_one', searchTerm.value)
  }
  return translate('fe_app_search_results_for_many', total, searchTerm.value)
})

// category.code is only set after a search that rendered results (incl. zero
// hits) — it stays empty on errors, redirects and mid-fetch navigation.
const trackSearchResults = () => {
  if (searchConfig.config.error || !category.value.code) return
  pushWalkerEvent('search results', {
    total_count: searchConfig.getConfig.totalCount ?? 0
  })
}

const runSearch = async () => {
  const term = searchTerm.value
  await searchConfig.fetchGlobalSearch()
  // Skip tracking when the fetch ended in a redirect or a newer search
  // superseded this one — the config would hold another search's count.
  if (searchTerm.value === term) trackSearchResults()
}

// `immediate` covers the initial mount; without a search term the BE responds
// 400, so the empty state renders instead of fetching.
watch(
  () => route.query.q,
  async (newQ, oldQ) => {
    if (newQ === oldQ) return
    // Keep the previous results mounted while the new search loads — a full
    // reset blanked the page for seconds on slow queries (VIP-12032).
    if (!newQ) {
      searchConfig.resetConfig()
      return
    }
    await runSearch()
  },
  { immediate: true }
)

onBeforeRouteLeave(() => {
  searchConfig.resetConfig()
})

useHead({
  title: computed(() =>
    searchTerm.value
      ? translate('fe_app_search_results_for', searchTerm.value)
      : translate('fe_app_search_results')
  ),
  meta: [
    {
      name: 'robots',
      content: NO_INDEX_FOLLOW
    }
  ]
})
</script>

<template>
  <div
    class="position-relative"
    id="content"
    :data-elbglobals="elbGlobals"
    data-elbcontext="shopping:discover"
  >
    <AppBanner />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader class="position-relative" />
    <main id="page-template">
      <div class="v-container my-5">
        <div class="v-col-12 py-5">
          <h1
            class="font-garbata text-center font-size-xxxxl font-size-md-xxxxxl font-weight-bold c-winered"
          >
            {{ headline }}
          </h1>
        </div>
      </div>
      <div
        v-if="searchTerm && manufacturerHits.length"
        class="v-container mb-6"
      >
        <h2 class="font-garbata font-size-xxl font-weight-bold c-winered mb-3">
          {{ translate('fe_app_search_wineries') }}
        </h2>
        <div class="manufacturer-strip d-flex">
          <ManufacturerChip
            v-for="man in manufacturerHits"
            :key="`man-${man.manufacturer_id}`"
            :manufacturer="man"
            class="flex-shrink-0"
          />
        </div>
      </div>
      <p v-if="categoryError" class="text-center py-10">
        {{ translate('cms_loading_error') }}
      </p>
      <TheFilter v-else-if="searchTerm && category.code" />
      <LoadingOverlay
        v-else-if="searchTerm && isLoading"
        width="70"
        height="70"
        :use-background="false"
        class="position-relative my-10 initial-search-loading"
      />
      <div v-else-if="!searchTerm" class="v-container text-center py-10">
        <p>{{ translate('fe_app_search_enter_term') }}</p>
      </div>
    </main>
    <LazyComponent id="footer" :delay="2000">
      <TheFooter />
    </LazyComponent>
  </div>
</template>

<style scoped>
/* Fill the viewport so the footer's LazyComponent placeholder spinner stays
   below the fold — otherwise two loading circles show during the initial
   search (VIP-12032). */
.initial-search-loading {
  min-height: 50vh;
}

.manufacturer-strip {
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}

.manufacturer-strip > * {
  scroll-snap-align: start;
  flex-shrink: 0;
}
</style>
